/**
 * Insights list — public /insights page.
 * SEO-friendly card grid, filters by category, cross-links to landings.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, ArrowRight, Clock, Tag, Sparkles, ShieldCheck, Brain, Filter } from 'lucide-react';
import useMetaTags from '../../hooks/useMetaTags';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  ai_act: { label: 'EU AI Act', tone: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30', icon: Brain },
  nis2:   { label: 'NIS2', tone: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', icon: ShieldCheck },
  both:   { label: 'NIS2 + AI Act', tone: 'bg-sky-500/15 text-sky-300 border-sky-500/30', icon: Sparkles },
};

export default function InsightsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [total, setTotal] = useState(0);

  useMetaTags({
    title: 'Insights · NIS2AI — Análises práticas NIS2 e EU AI Act',
    description: 'Casos práticos, guias e análises actualizadas sobre compliance NIS2 (Lei 125/2025) e Regulamento UE 2024/1689 (AI Act). Direcionado a CISOs, DPOs e legal officers.',
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const params = { limit: 24 };
        if (category !== 'all') params.category = category;
        const { data } = await axios.get(`${BACKEND_URL}/api/public/insights`, { params });
        if (mounted) { setItems(data.items); setTotal(data.total); }
      } catch (err) {
        if (mounted) setItems([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [category]);

  return (
    <div className="min-h-screen bg-[#080d18] text-slate-100 relative">
      {/* Ambient */}
      <div className="pointer-events-none fixed inset-0 opacity-30">
        <div className="absolute top-[-20%] right-[-10%] h-[500px] w-[500px] rounded-full bg-indigo-600/20 blur-[120px]" />
        <div className="absolute bottom-[-15%] left-[-10%] h-[500px] w-[500px] rounded-full bg-emerald-500/15 blur-[130px]" />
      </div>

      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5 border-b border-white/5">
        <Link to="/insights" className="flex items-center gap-2 font-semibold tracking-tight" data-testid="insights-logo">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-slate-700 to-slate-500 flex items-center justify-center">
            <BookOpen className="h-4 w-4" />
          </div>
          <span className="text-lg">NIS2<span className="text-slate-300">AI</span> · Insights</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/landing/ai-act" className="text-sm text-slate-400 hover:text-white transition-colors hidden md:inline">AI Act</Link>
          <Link to="/landing/nis2" className="text-sm text-slate-400 hover:text-white transition-colors hidden md:inline">NIS2</Link>
          <Link to="/trust" className="text-sm text-slate-400 hover:text-white transition-colors hidden md:inline">Trust</Link>
          <Link to="/login" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="insights-login-link">Entrar</Link>
        </div>
      </nav>

      <main className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-14 md:py-20">
        {/* Hero */}
        <div className="max-w-3xl mb-14" data-testid="insights-hero">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Insights & análises</p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight leading-[1.1]">
            Compliance NIS2 e AI Act,
            <span className="block bg-gradient-to-r from-indigo-400 via-emerald-400 to-sky-400 bg-clip-text text-transparent">explicado por quem opera.</span>
          </h1>
          <p className="mt-4 text-base md:text-lg text-slate-400 leading-relaxed">
            Casos práticos, referências a artigos concretos dos regulamentos, e passos accionáveis. Sem hype, sem venda.
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-8 flex-wrap" data-testid="insights-filters">
          <Filter className="h-4 w-4 text-slate-500 mr-1" />
          {['all', 'nis2', 'ai_act', 'both'].map(c => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                category === c
                  ? 'bg-white text-slate-900'
                  : 'bg-white/5 text-slate-300 hover:bg-white/10'
              }`}
              data-testid={`insights-filter-${c}`}
            >
              {c === 'all' ? 'Todos' : CATEGORY_META[c]?.label}
            </button>
          ))}
          <span className="text-xs text-slate-500 ml-auto">{loading ? '...' : `${total} artigos`}</span>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-64 rounded-2xl border border-white/5 bg-white/[0.02] animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 rounded-2xl border border-white/5 bg-white/[0.02]">
            <BookOpen className="h-10 w-10 mx-auto text-slate-600 mb-3" />
            <p className="text-slate-400">Ainda sem artigos publicados nesta categoria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="insights-grid">
            {items.map(it => {
              const cat = CATEGORY_META[it.category] || CATEGORY_META.both;
              const CatIcon = cat.icon;
              return (
                <Link
                  key={it.insight_id}
                  to={`/insights/${it.slug}`}
                  className="group p-6 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20 backdrop-blur-md transition-all"
                  data-testid={`insight-card-${it.slug}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${cat.tone}`}>
                      <CatIcon className="h-3 w-3" /> {cat.label}
                    </span>
                    <span className="text-2xl">{it.hero_emoji || '📄'}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-100 leading-snug group-hover:text-white line-clamp-3">
                    {it.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed line-clamp-3">{it.excerpt}</p>
                  <div className="mt-5 flex items-center justify-between text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{it.read_time_min || 3} min</span>
                    <span className="inline-flex items-center gap-1 text-indigo-400 group-hover:text-indigo-300">
                      Ler <ArrowRight className="h-3 w-3" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <section className="mt-20 p-8 md:p-12 rounded-3xl border border-white/10 bg-gradient-to-br from-indigo-500/10 to-emerald-500/10 backdrop-blur-md text-center">
          <Sparkles className="h-8 w-8 mx-auto text-indigo-400 mb-3" />
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">Faz um diagnóstico em 90 segundos</h2>
          <p className="mt-2 text-sm text-slate-300 max-w-xl mx-auto">
            Descobre a exposição regulatória da tua organização com um dos self-assessments gratuitos.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/landing/nis2" className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-slate-100 rounded-full font-semibold h-11 px-6 text-sm">
              <ShieldCheck className="h-4 w-4" /> Self-Assessment NIS2 <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/landing/ai-act" className="inline-flex items-center gap-2 bg-slate-800 text-slate-100 hover:bg-slate-700 rounded-full font-semibold h-11 px-6 text-sm">
              <Brain className="h-4 w-4" /> Self-Assessment AI Act <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-white/5 py-8 px-6 text-center text-xs text-slate-500">
        <p>© {new Date().getFullYear()} NIS2AI · Insights ·{' '}
          <Link to="/trust" className="hover:text-white underline underline-offset-2">Trust Center</Link>
        </p>
      </footer>
    </div>
  );
}
